import { useRef, useState } from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { type Swiper as SwiperRef } from 'swiper';
import { Box, Chip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { MediaSlider as IMediaSlider, Video, MediaSliderContentsItem } from '@/types/generated';
import { Maybe } from '@/types';
import AspectRatioPaddingBox from '@/components/AspectRatioPaddingBox';
import FocalPointImage from '@/components/FocalPointImage/FocalPointImage';
import VideoContent from '@/components/VideoContent/VideoContent';
import TAGSvgIcon from '@/components/TAGSvgIcon';

import {
	badgeStyles,
	manualRecordIconStyles,
	manualRecordIconWrapper,
	swiperActionsWrapper,
	getWrapperStyles,
	getSwiperStyles,
	buttonStyles,
} from './MediaSlider.styles';

interface IProps extends Partial<IMediaSlider> {
	fullHeight?: boolean;
	enableSliderControlsBackgroundGradient?: boolean;
	/**
	 * Image sizes attribute for responsive images
	 */
	imageSizes?: string;
}

export default function MediaSlider({
	contentsCollection,
	badge,
	fullHeight,
	enableSliderControlsBackgroundGradient,
	imageSizes,
}: IProps) {
	const swiperRef = useRef<SwiperRef>();
	const [activeStep, setActiveStep] = useState(0);
	const theme = useTheme();
	const isSmall: boolean = useMediaQuery(theme.breakpoints.down('md'));

	const mediaContents = contentsCollection?.items;

	const handleStepChange = (step: number) => {
		setActiveStep(step);
	};

	if (!mediaContents?.length) {
		return null;
	}

	return (
		<Box data-testid="media_slider" sx={getWrapperStyles(fullHeight)}>
			{badge && (
				<Chip
					label={badge.label}
					variant="heavyDefault" // to do: export type from theme package and then change to badge.variant
					sx={badgeStyles}
					data-testid="media_slider_badge"
				/>
			)}
			<Swiper
				onSlideChange={(swiper) => handleStepChange(swiper.activeIndex)}
				onSwiper={(swiper) => {
					swiperRef.current = swiper;
				}}
				style={getSwiperStyles(fullHeight)}
			>
				{mediaContents.map((media: Maybe<MediaSliderContentsItem>, index: number) => {
					if (media) {
						return (
							<SwiperSlide key={`swiper_media_${index}`}>
								<AspectRatioPaddingBox aspectRatio="1:1">
									{media.__typename === 'FocalPointImage' ? (
										<FocalPointImage {...media} dataTestId={`media_focal_image_${media.sys.id}`} sizes={imageSizes ?? undefined} 
										loading= {index === 0 ? undefined : 'lazy'}
										fetchPriority={index === 0 ? 'high' : 'auto'}
										
										/>
									) : (
										<VideoContent
											dataTestId={`media_video_${media.sys.id}`}
											controllableVideo={media as Video}
											videoControlsSx={{ bottom: '3.85rem' }}
										/>
									)}
								</AspectRatioPaddingBox>
							</SwiperSlide>
						);
					}
					return null;
				})}
			</Swiper>
			{mediaContents.length > 1 ? (
				<Box
					sx={swiperActionsWrapper(enableSliderControlsBackgroundGradient)}
					data-testid="media_slider_actions"
				>
					<IconButton
						onClick={() => swiperRef.current?.slidePrev()}
						sx={buttonStyles(theme)}
						disabled={activeStep === 0}
						data-testid="media_slider_left_chevron_button"
						aria-label="Previous slide"
					>
						<TAGSvgIcon icon="ChevronLeftFunc" size={isSmall ? 32 : 40} />
					</IconButton>

					<Box sx={manualRecordIconWrapper}>
						{mediaContents.map((_, index) => (
							<button
								key={`manual_record_icon_${index}`}
								data-testid={`media_slider_manual_record_icon_${index}`}
								onClick={() => swiperRef.current?.slideTo(index)}
								aria-label={`Go to slide ${index + 1}`}
								type="button"
								style={{
									background: 'none',
									border: 'none',
									padding: 0,
									cursor: 'pointer',
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<FiberManualRecordIcon sx={manualRecordIconStyles(index, activeStep, theme)} />
							</button>
						))}
					</Box>

					<IconButton
						onClick={() => swiperRef.current?.slideNext()}
						sx={buttonStyles(theme)}
						disabled={activeStep === mediaContents.length - 1}
						data-testid="media_slider_right_chevron_button"
						aria-label="Next slide"
					>
						<TAGSvgIcon icon="ChevronRightFunc" size={isSmall ? 32 : 40} />
					</IconButton>
				</Box>
			) : null}
		</Box>
	);
}
